import { UserProfileCompletionProgress } from '@/components/account/UserProfile';
import { EntityCard } from '@/components/cards/EntityCard';
import { Button } from '@/components/ui/Button';
import withLocation from '@/components/withLocation';
import { DASHBOARD_NAVIGATION_PATHS } from '@/lib/constants/dashboardNavigationPaths';
import { transformContent } from '@/lib/helpers/content';
import { getUserProfileDisplayName } from '@/lib/helpers/userProfile';
import { cn } from '@/lib/helpers/utils';
import { Organisation } from '@/types/content';
import { WindowLocation } from '@reach/router';
import { Link } from 'gatsby';
import { chunk, isEmpty } from 'lodash';
import { ArrowRight, Search } from 'lucide-react';
import React from 'react';
import { useAuthSession } from '../../../app/queries/authSession';
import { mergeDeep } from '../../../app/utils/deepMerge';
import { removeNonAlpaChars } from '../../../app/utils/string';
import { asyncTrackingEvent, asyncTrackingEventProps } from '../../../app/utils/tracking';
import { ImageTypes } from '../../../types/cards';
import { CarouselBlock } from '../blocks/CarouselBlock';
import Logos from '../blocks/Logos';

/**
 * Tracking when the sidebar cards are clicked.
 * @param data {any}
 * @param label {string}
 */
const onSidebarCardClick = (data: any, label: string): void => {
  const event: asyncTrackingEventProps = {
    track: 'sidebar_card_click',
    trackAbacus: 'sidebar_card_click',
    trackValue: label,
    trackText: data.title,
    trackId: data.uuid,
    trackTarget: data.path.alias
  };
  asyncTrackingEvent(event);
};

/**
 * Tracking when related roles cards are clicked.
 * @param data {any}
 */
const onRelatedRolesClick = (data: any): void => {
  const event: asyncTrackingEventProps = {
    track: 'related_roles_click',
    trackAbacus: 'related_roles_click',
    trackId: data.uuid,
    trackUrl: data.path.alias
  };
  asyncTrackingEvent(event);
};

/**
 * Wraps content for each section with additional html.
 * @param content {any}
 * @param label {string}
 * @param cardSpacing {string}
 * @param labelSpacing {string}
 * @return {JSX}
 */
const panelWrapper = (
  isSignedIn: boolean,
  content: any,
  label: string,
  contentContainerClasses: string = '',
  cardClasses?: string
) => {
  return (
    <div
      className={cn('w-full py-4 bg-white border-b border-gray-200', cardClasses)}
      key={`sidebar_panel_${isSignedIn ? 'loggedIn' : 'loggedOut'}_${label}`}
      data-cy={`section:inspiration-bar-${removeNonAlpaChars(label)}`}
    >
      <h2 className={`display-title-lg pb-2 mb-4 border-b-red border-b`}>{label}</h2>
      <div
        className={`divide-y divide-deep-100 ${contentContainerClasses}`}
        key={`sidebar_panel_content_${isSignedIn ? 'loggedIn' : 'loggedOut'}_${label}`}
      >
        {content}
      </div>
    </div>
  );
};

/**
 * Renders the IDS slider
 * @param slides {any}
 * @param sliderId {string}
 * @param maxSlidesToDisplay {number}
 * @param groupOptions {any}
 * @return {JSX}
 */
const sidebarSlider = (cards: any, sliderId: string, maxSlidesToDisplay: number = 5, groupOptions: any = null) => {
  let slides = cards;
  if (groupOptions) {
    slides = chunk(cards, groupOptions.cardsPerSlide);
  }
  return (
    <CarouselBlock
      slides={slides.slice(0, maxSlidesToDisplay).map((item: any, index: number) => {
        return (
          <SidebarCardContainer
            key={`slide-${sliderId}-${index}`}
            classes="slide w-full px-4"
            groupOptions={groupOptions}
          >
            {item}
          </SidebarCardContainer>
        );
      })}
      carouselItemProps={{ className: 'basis-full' }}
      carouselPreviousButtonProps={{ size: 'sm' }}
      carouselNextButtonProps={{ size: 'sm' }}
      showDots={true}
    />
  );
};

/**
 * Wrapper around the collection of cards, this maybe on one card, if its a slide.
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.groupOptions - Options for grouping cards
 * @param {string} props.classes - Additional CSS classes
 * @returns {JSX.Element} Sidebar card container
 */
const SidebarCardContainer = ({ children, groupOptions, classes }: any) => {
  return (
    <div
      className={`${classes ? classes : ''} ${
        groupOptions && groupOptions.seperatorClass ? groupOptions.seperatorClass : ''
      }`}
    >
      {children}
    </div>
  );
};

/**
 * Returns all the cards that are needed for a specific side bar section
 * @param {string} panelReference - Reference to the panel
 * @param {Array<any>} content - Array of content items
 * @param {string} sectionLabel - Label for the section
 * @param {Function} [dataMapper] - Optional function to map data
 * @param {any} [customHtml] - Optional custom HTML
 * @returns {Array<JSX.Element>} Array of sidebar cards
 */
const getSidebarCards = (
  panelReference: string,
  content: Array<any>,
  sectionLabel: string,
  dataMapper?: Function,
  customHtml?: any
) => {
  if (!isEmpty(content)) {
    const cards = content.map((result: any, i: number) =>
      getSidebarCard(panelReference, result, sectionLabel, i, dataMapper, customHtml)
    );
    return cards;
  } else {
    return [];
  }
};

/**
 * Return a card for a specific content type
 * @param {string} panelReference - Reference to the panel
 * @param {any} item - Content item
 * @param {string} sectionLabel - Label for the section
 * @param {number} index - Index of the card
 * @param {Function} [dataMapper] - Optional function to map data
 * @param {any} [customHtml] - Optional custom HTML
 * @returns {JSX.Element} Sidebar card
 */
const getSidebarCard = (
  panelReference: string,
  item: any,
  sectionLabel: string,
  index: number,
  dataMapper?: Function,
  customHtml?: any
) => {
  const content = { ...item };

  let banner = item.relationships.field_featured_image?.localFile?.childImageSharp?.gatsbyImageData
    ? {
        type: ImageTypes.gatsby,
        image: item.relationships.field_featured_image.localFile.childImageSharp.gatsbyImageData,
        alt: `Banner for ${item.title}`
      }
    : null;

  let thumbnail = item.relationships.field_thumbnail_image?.localFile?.childImageSharp?.gatsbyImageData
    ? {
        type: ImageTypes.gatsby,
        image: item.relationships.field_thumbnail_image.localFile.childImageSharp.gatsbyImageData,
        alt: `Thumbnail for ${item.title}`
      }
    : null;

  let logo = item.relationships.field_logo?.localFile?.childImageSharp?.gatsbyImageData
    ? {
        type: ImageTypes.gatsby,
        image: item.relationships.field_logo.localFile.childImageSharp.gatsbyImageData,
        alt: `Logo for ${item.title}`
      }
    : null;

  const profile = item.relationships.field_graduate_image?.localFile?.childImageSharp?.gatsbyImageData
    ? {
        type: ImageTypes.gatsby,
        image: item.relationships.field_graduate_image.localFile.childImageSharp.gatsbyImageData,
        alt: `Profile for ${item.title}`
      }
    : null;

  // Determine if we are needed to add reference to organisation / education provider logo from relationship.
  const related = item.relationships?.field_organisation
    ? item.relationships.field_organisation
    : item.relationships?.field_education_provider;
  if (related && related.title) {
    logo = related.relationships?.field_logo?.localFile?.childImageSharp?.gatsbyImageData
      ? {
          type: ImageTypes.gatsby,
          image: related.relationships.field_logo.localFile.childImageSharp.gatsbyImageData,
          alt: `Logo for ${related.title}`
        }
      : logo;
    if (item.type === 'opportunity' || item.type === 'course') {
      banner = related.relationships?.field_featured_image?.localFile?.childImageSharp?.gatsbyImageData
        ? {
            type: ImageTypes.gatsby,
            image: related.relationships?.field_featured_image.localFile.childImageSharp.gatsbyImageData,
            alt: `Banner for ${related.title}`
          }
        : banner;
      thumbnail = related.relationships?.field_thumbnail_image?.localFile?.childImageSharp?.gatsbyImageData
        ? {
            type: ImageTypes.gatsby,
            image: related.relationships?.field_thumbnail_image.localFile.childImageSharp.gatsbyImageData,
            alt: `Thumbnail for ${related.title}`
          }
        : null;
    }
  }

  // Data from data mapper
  let mappedData = {
    attributes: {} as any
  };
  if (dataMapper) {
    mappedData = mergeDeep(mappedData, dataMapper(content));
  }

  // Determine tracking handler based on panel type
  const trackingHandler: React.MouseEventHandler<HTMLDivElement> =
    panelReference === 'RelatedOpportunityPanel'
      ? () => onRelatedRolesClick(item)
      : () => onSidebarCardClick(item, sectionLabel);

  // Override card heading to be smaller
  // content.title = `<span class="text-base text-gray leading-5 block pb-0.5 group-hover:text-primary">${content.title}</span>`;
  const card = renderCardByPanelAndType({
    panelReference,
    content,
    images: { banner, logo, thumbnail, profile },
    onClick: trackingHandler,
    index,
    customHtml
  });

  return (
    <div
      key={sectionLabel + '_' + item.uuid}
      className={`${
        mappedData.attributes.customSidebarCardClass ? mappedData.attributes.customSidebarCardClass : ''
      } group`}
    >
      {card}
    </div>
  );
};

/**
 * Sidebar sections
 */

/**
 * Displays the top picks panel
 * @param {boolean} isSignedIn - Whether the user is signed in
 * @param {any} content - Content to display
 * @param {string} [cardClasses] - Optional CSS classes for cards
 * @returns {JSX.Element} Top picks panel
 */
const displayTopPicksPanel = (isSignedIn: boolean, content: any, cardClasses?: string) => {
  const sectionLabel = 'Our top picks';
  const cardLimit = 5;
  const cards = getSidebarCards('TopPicksPanel', content, sectionLabel);
  const slider = sidebarSlider(cards, 'toppicks-slider', cardLimit);
  return content.length > 0 && panelWrapper(isSignedIn, slider, sectionLabel, '-mx-4', cardClasses);
};

/**
 * Displays the related opportunities panel
 * @param {boolean} isSignedIn - Whether the user is signed in
 * @param {any} content - Content to display
 * @param {string} [cardClasses] - Optional CSS classes for cards
 * @returns {JSX.Element} Related opportunities panel
 */
const displayRelatedOpportunityPanel = (isSignedIn: boolean, content: any, cardClasses?: string) => {
  const sectionLabel = 'Related roles';
  const cardsPerSlide = 3;
  const cardLimit = 12;
  const cards = getSidebarCards('RelatedOpportunityPanel', content, sectionLabel, (_item: any) => {
    return {
      attributes: {
        customSidebarCardClass: `pt-4 first-of-type:pt-0 last-of-type:pb-4 ${
          content.length > cardsPerSlide
            ? 'last-of-type:!border-b last-of-type:!border-gray-400 last-of-type:!pb-4'
            : ''
        }`
      }
    };
  });
  const slider = sidebarSlider(cards, 'opp-slider', cardLimit, {
    cardsPerSlide,
    seperatorClass: `space-y-4 divide-y divide-gray-300`
  });
  return content.length > 0 && panelWrapper(isSignedIn, slider, sectionLabel, '-mx-4', cardClasses);
};

/**
 * Displays the personal panel with login CTA
 * @param {boolean} isSignedIn - Whether the user is signed in
 * @param {boolean} [displayPersonal=true] - Whether to display personal content
 * @param {User} [user] - User object
 * @returns {JSX.Element} Personal panel
 */
const displayPersonalPanel = (isSignedIn: boolean, displayPersonal: boolean = true) => {
  return (
    <div
      key={`sidebar_personalPanel_${isSignedIn ? 'loggedIn' : 'loggedOut'}_${
        displayPersonal ? 'withPersonal' : 'withoutPersonal'
      }`}
      data-cy={`section:inspiration-bar-personal-panel`}
    >
      {displayPersonal && !isSignedIn && (
        <Button asChild size="sm" appearance="secondary" className="w-full justify-between">
          <Link to="/login" data-cy={`link:view-log-in`}>
            <span>Get results tailored to you</span>
            <div className="flex space-x-1">
              <ArrowRight className="w-4" />
              <span className="text-white">Log in</span>
            </div>
          </Link>
        </Button>
      )}
    </div>
  );
};

/**
 * Displays the general content panel
 * @param {boolean} isSignedIn - Whether the user is signed in
 * @param {any} content - Content to display
 * @param {string} [cardClasses] - Optional CSS classes for cards
 * @returns {JSX.Element} General content panel
 */
const displayGeneralPanel = (isSignedIn: boolean, content: any, cardClasses?: string) => {
  const sectionLabel = 'You might be interested in...';
  const cardLimit = 5;
  const cards = getSidebarCards('GeneralPanel', content, sectionLabel, (_item: any) => {
    return {
      attributes: {
        customSidebarCardClass: 'pt-4 first-of-type:pt-0 last-of-type:pb-0'
      }
    };
  });

  const container = (
    <SidebarCardContainer key={sectionLabel} classes="space-y-4 divide-y divide-gray-300">
      {cards.slice(0, cardLimit)}
    </SidebarCardContainer>
  );

  return content.length > 0 && panelWrapper(isSignedIn, container, sectionLabel, cardClasses);
};

/**
 * Displays the employer branding panel
 * @param {string} title - Panel title
 * @param {any} content - Content to display
 * @param {boolean} [showMore] - Whether to show "show more" button
 * @returns {JSX.Element} Employer branding panel
 */
const displayEmployerBrandingPanel = (title: string, content: any, showMore?: boolean) => {
  return content && content.length > 0 ? (
    <div>
      <div className="bg-gradient-to-b from-gray to-[#4B4B4B] p-3 rounded-md shadow-sm ">
        {title && <h4 className="font-normal text-center text-white text-xs tracking-wider mt-1 mb-3">{title}</h4>}
        <Logos
          logos={content.slice(0, 4)}
          logosToShowCount={4}
          splitColumns={false}
          hideShowMore={true}
          logoSize={'w-16 h-16'}
          classOverrides={'!gap-3'}
        />
      </div>
      {showMore && (
        <Button asChild size="sm" appearance="gray" variant="outline" className="mt-2">
          <Link to="/search/employers?search=" data-cy="link:all-employers" className="w-full">
            <Search className="w-4" />
            <span>All employers</span>
          </Link>
        </Button>
      )}
    </div>
  ) : null;
};

/**
 * Displays the trending panel
 * @param {boolean} isSignedIn - Whether the user is signed in
 * @param {any} content - Content to display
 * @param {Array<{label: string}>} [sectors] - Array of sectors
 * @param {string} [brandName] - Brand name
 * @param {string} [cardClasses] - Optional CSS classes for cards
 * @returns {JSX.Element} Trending panel
 */
const displayTrendingPanel = (
  isSignedIn: boolean,
  content: any,
  sectors: Array<{ label: string }> = [],
  brandName: string = '',
  cardClasses?: string
) => {
  const sectionLabel =
    sectors && sectors.length === 1
      ? `Trending in ${sectors[0].label}`
      : sectors && sectors.length > 0
        ? `Trending in these sectors`
        : `Trending on ${brandName}`;
  const cardLimit = 5;
  const cards = getSidebarCards('TrendingPanel', content, sectionLabel);

  const container = (
    <SidebarCardContainer key={sectionLabel} classes="space-y-4 divide-y divide-gray-300">
      {cards.slice(0, cardLimit).map((card: any, i: number) => {
        return (
          <div key={sectionLabel + '_' + i} className="flex space-x-3">
            <div>
              <p className="text-2xl text-secondary-1100 font-semibold leading-6 mt-1">{i + 1}</p>
            </div>
            <div className="w-full">{card}</div>
          </div>
        );
      })}
    </SidebarCardContainer>
  );

  return content.length > 0 && panelWrapper(isSignedIn, container, sectionLabel, cardClasses);
};

/**
 * Displays the organisations panel
 * @param {boolean} isSignedIn - Whether the user is signed in
 * @param {any} content - Content to display
 * @param {string} sectionLabel - Label for the section
 * @param {string} [cardClasses] - Optional CSS classes for cards
 * @param {any} [customHtml] - Optional custom HTML
 * @returns {JSX.Element} Organisations panel
 */
const displayOrganisationsPanel = (
  isSignedIn: boolean,
  content: any,
  sectionLabel: string,
  cardClasses?: string,
  customHtml?: any
) => {
  const cardsPerSlide = 5;
  const cardLimit = 15;
  const cards = getSidebarCards(
    'OrganisationsPanel',
    content,
    sectionLabel,
    (item: any) => {
      return {
        attributes: {
          iconOnlyActions: true,
          suppressTracking: true,
          customSidebarCardClass: `pt-4 first-of-type:pt-0 last-of-type:pb-4 ${
            content.length > cardsPerSlide
              ? 'last-of-type:!border-b last-of-type:!border-gray-400 last-of-type:!pb-4'
              : ''
          }`,
          inlineActions: true
        },
        config: {
          excludes: ['description', 'thumbnail', 'banner']
        }
      };
    },
    customHtml
  );
  const slider = sidebarSlider(cards, 'opp-slider', cardLimit, {
    cardsPerSlide,
    seperatorClass: `space-y-4 divide-y divide-gray-300`
  });
  return content.length > 0 && panelWrapper(isSignedIn, slider, sectionLabel, '-mx-4', cardClasses);
};

/**
 * *****************************************************************************************
 */

type RenderProps = {
  panelReference: string;
  content: any;
  images: any;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  index: number;
  children?: any;
  customHtml?: any;
};

/**
 * Renders the corresponding cards relating to the panel and type
 * @param {RenderProps} args - Render properties
 * @returns {JSX.Element} Rendered card
 */
const renderCardByPanelAndType = (args: RenderProps) => {
  const cardKey = `${args.panelReference}_${args.index}_${args.content.uuid}`;

  /**
   * Simple organisation cards, used by OrganisationsPanel
   * @param args {RenderProps}
   * @return {JSX}
   */
  const simpleOrganisationCard = (args: RenderProps) => {
    return (
      <EntityCard
        key={cardKey}
        content={{
          ...transformContent<Organisation>({
            ...args.content,
            showEditorialAdvice: true
          })
        }}
        exclude={{
          [args.content.type]: ['thumbnail', 'details', 'tracking']
        }}
        className="border-none rounded-lg hover:bg-transparent"
        contentProps={{
          className: 'px-0 py-0 rounded-b-xl'
        }}
        thumbnailProps={{
          className: args.content.type === 'video' ? 'rounded-b-xl h-auto' : 'rounded-b-xl'
        }}
        onClick={args.onClick}
      />
    );
  };

  /**
   * Simple trending cards, used by TrendingPanel
   * @param args {RenderProps}
   * @return {JSX}
   */
  const simpleTrendingCard = (args: RenderProps) => {
    return (
      <EntityCard
        key={cardKey}
        content={transformContent<any>({ ...args.content })}
        exclude={{
          [args.content.type]: ['thumbnail', 'details', 'description', 'sectors', 'associatedOrg', 'tracking']
        }}
        className="border-none rounded-lg hover:bg-transparent"
        contentProps={{
          className: 'px-0 pb-0 pt-0'
        }}
        onClick={args.onClick}
      />
    );
  };

  // Determine what card type to display.
  let card;
  switch (args.panelReference.toLowerCase()) {
    case 'organisationspanel':
      card = simpleOrganisationCard(args);
      break;
    case 'trendingpanel':
      card = simpleTrendingCard(args);
      break;

    default:
      card = (
        <EntityCard
          key={cardKey}
          content={{
            ...transformContent<any>({
              ...args.content,
              showEditorialAdvice: true
            })
          }}
          exclude={{
            [args.content.type]: [
              'abstract',
              'body',
              'sectors',
              'logo',
              'description',
              'tracking',
              ...(args.content.type === 'opportunity' ? ['thumbnail', 'location'] : [])
            ]
          }}
          className="border-none rounded-lg hover:bg-transparent"
          contentProps={{
            className: 'px-0 pb-0'
          }}
          thumbnailProps={{
            className: args.content.type === 'video' ? 'rounded-b-xl h-auto' : 'rounded-b-xl'
          }}
          onClick={args.onClick}
        />
      );
      break;
  }

  return card;
};

/**
 * *****************************************************************************************
 */

type SidebarInspirationProps = {
  topPicks?: any;
  recommendations?: any;
  sectors?: Array<{ label: string }>;
  brandName?: string;
  enterpriseOrgs?: any;
  professionalOrgs?: any;
  partnershipOrgs?: any;
  enterpriseOrgsNoSectors?: any;
  professionalOrgsNoSectors?: any;
  partnershipOrgsNoSectors?: any;
  displayPersonal?: boolean;
  orgPanelTitle?: string;
  hideEmployerBranding?: boolean;
  hideTopPicksPanel?: boolean;
  hideTrendingPanel?: boolean;
  hideOrganisationsPanel?: boolean;
  cardClasses?: string;
  containerClasses?: string;
  customHtml?: any;
  location: WindowLocation<unknown>;
};

/**
 * Renders out the SidebarInspiration
 * @return {JSX}
 */
const SidebarInspiration = ({
  topPicks,
  recommendations,
  sectors,
  brandName,
  enterpriseOrgs,
  professionalOrgs,
  partnershipOrgs,
  enterpriseOrgsNoSectors,
  professionalOrgsNoSectors,
  partnershipOrgsNoSectors,
  orgPanelTitle,
  hideEmployerBranding,
  hideTrendingPanel,
  hideTopPicksPanel,
  hideOrganisationsPanel,
  cardClasses = '',
  displayPersonal = true,
  containerClasses,
  customHtml,
  location
}: SidebarInspirationProps) => {
  const { data: authSession } = useAuthSession();
  const isSignedIn = authSession?.isAuthenticated ?? false;
  const authLevel = authSession?.authLevel ?? 'NONE';
  const user = authSession?.isAuthenticated ? authSession.user : undefined;

  type LogoParagraphType = {
    path?: any;
    id: string;
    type: string;
    title: string;
    image: any;
    alias: any;
  };

  /**
   * Organisation subscription data
   * @param level string
   * @param showUnpublished boolean
   * @return {JSX}
   */
  const organisationData = (level: any, showUnpublished?: boolean) => {
    const getSubData = (level: any) => {
      let temp = null;
      switch (level.toString()) {
        case 'Partnership': {
          temp = sectors && sectors.length > 1 ? partnershipOrgs : partnershipOrgsNoSectors;
          break;
        }
        case 'Professional': {
          temp = sectors && sectors.length > 1 ? professionalOrgs : professionalOrgsNoSectors;
          break;
        }
        case 'Enterprise': {
          temp = sectors && sectors.length > 1 ? enterpriseOrgs : enterpriseOrgsNoSectors;
          break;
        }
        default: {
          temp = null;
          break;
        }
      }

      return showUnpublished
        ? temp?.nodes
        : temp?.nodes.filter(function (org: any) {
            return org.status === '1';
          });
    };

    const subData = getSubData(level);
    return subData && subData.length > 0
      ? subData.map((logo: any) => {
          return {
            id: logo.id,
            title: logo.title,
            image: logo.relationships.medium_logo?.localFile?.childImageSharp?.gatsbyImageData,
            alias: logo.status == 1 ? logo.path.alias : null
          } as LogoParagraphType;
        })
      : null;
  };

  return (
    <div
      className="mb-4 space-y-5"
      key={`sidebar_${isSignedIn ? 'loggedIn' : 'loggedOut'}`}
      data-cy={`section:inspiration-bar`}
    >
      {isSignedIn && !isEmpty(user) && location.pathname.indexOf(DASHBOARD_NAVIGATION_PATHS.root) === -1 && (
        <div className="hidden space-y-1 p-4 bg-surface-50 rounded-2xl md:block">
          <p className="text-gray font-bold leading-6">{getUserProfileDisplayName(user as any, authLevel as any)}</p>
          <UserProfileCompletionProgress user={user as any} />
        </div>
      )}
      <div className={cn('space-y-5', containerClasses)}>
        {!hideTopPicksPanel ? displayTopPicksPanel(isSignedIn, topPicks ?? [], cardClasses) : null}
        {!hideEmployerBranding
          ? displayEmployerBrandingPanel('INDUSTRY LEADERS', organisationData(['Partnership']))
          : null}
        {displayRelatedOpportunityPanel(isSignedIn, recommendations?.opportunities ?? [], cardClasses)}
        {displayPersonalPanel(isSignedIn, displayPersonal)}
        {!hideTrendingPanel
          ? displayTrendingPanel(isSignedIn, recommendations?.trendingArticles ?? [], sectors, brandName, cardClasses)
          : null}
        {!hideEmployerBranding
          ? displayEmployerBrandingPanel('FEATURED EMPLOYERS', organisationData(['Enterprise']))
          : null}
        {displayGeneralPanel(isSignedIn, recommendations?.generalContent ?? [], cardClasses)}
        {!hideEmployerBranding
          ? displayEmployerBrandingPanel('MORE EMPLOYERS', organisationData(['Professional']), true)
          : null}
        {!hideOrganisationsPanel
          ? displayOrganisationsPanel(
              isSignedIn,
              recommendations?.organisations ?? [],
              orgPanelTitle ?? 'Spotlight employers',
              cardClasses,
              customHtml
            )
          : null}
      </div>
    </div>
  );
};

export default withLocation(SidebarInspiration);
